import JSPDF from 'jspdf';
import { utils, writeFileXLSX } from 'xlsx';

import { OBLIGATION_RESULT_TYPE } from 'common/constants/AISimulationConstants';
import {
	calculateYCoordinate,
	checkNewPageNeeded,
	wrapText,
} from 'common/utils/utilFunctions';

import { IObligationToPolicyOutput } from '../api/type-def';

const margin = 10;
let currentY = margin;
const lineHeight = 6;

const setPDFStyles = (
	doc: JSPDF,
	result: IObligationToPolicyOutput,
	pageHeight: number,
	textWidth: number,
) => {
	const addNormalText = (
		type: 'normal' | 'bold',
		size: number,
		text?: string,
	) => {
		doc.setFontSize(size);
		doc.setFont('helvetica', type);
		currentY = checkNewPageNeeded(
			wrapText(doc, textWidth, text).length,
			currentY,
			lineHeight,
			pageHeight,
			margin,
			doc,
		);
		doc.text(wrapText(doc, textWidth, text), margin, currentY);
		currentY = calculateYCoordinate(
			wrapText(doc, textWidth, text).length,
			lineHeight,
			currentY,
		);
	};

	addNormalText('bold', 20, 'Obligations');
	addNormalText('normal', 14, result.obligation);

	addNormalText('bold', 20, 'Excerpts from the policies and procedures:');

	result.analysis.forEach((res) => {
		addNormalText('normal', 14, res.raw);
		addNormalText('normal', 14, `Source: ${res.source}`);
		addNormalText('normal', 14, `Analysis: ${res.analysis}`);
		addNormalText('normal', 14, `Result: ${res.result}`);
		addNormalText('normal', 14, `Remediation: ${res.remediation}`);
	});
};

const createPDFDoc = (
	analysis: IObligationToPolicyOutput[],
	doc: JSPDF,
	pageHeight: number,
	textWidth: number,
) => {
	doc.setFontSize(24);
	doc.setFont('helvetica', 'bold');
	currentY = checkNewPageNeeded(
		wrapText(doc, textWidth, 'Results').length,
		currentY,
		lineHeight,
		pageHeight,
		margin,
		doc,
	);
	doc.text(wrapText(doc, textWidth, 'Results'), margin, currentY);
	currentY = calculateYCoordinate(
		wrapText(doc, textWidth, 'Results').length,
		lineHeight,
		currentY,
	);
	analysis.map((result) => {
		result.analysis.slice().sort((a, b) => {
			const firstOrder = OBLIGATION_RESULT_TYPE.findIndex(
				(e) => e.result === a.result,
			);
			const secondOrder = OBLIGATION_RESULT_TYPE.findIndex(
				(e) => e.result === b.result,
			);
			return firstOrder - secondOrder;
		});
		return setPDFStyles(doc, result, pageHeight, textWidth);
	});
};
const generateExcelFile = (analysis: IObligationToPolicyOutput[]) => {
	const keyMapping: Record<string, string> = {
		obligation: 'Obligation',
		result: 'Match Result',
		source: 'Source',
		raw: 'Excerpt from Policy Document',
		analysis: 'Analysis',
		remediation: 'Remediation',
	};
	const desiredOrder = Object.values(keyMapping);
	// Utility function to map keys and reorder columns
	const mapAndReorder = (
		obj: Record<string, any>,
		mapping: Record<string, string>,
		order: string[],
	): Record<string, any> => {
		const mappedObj: Record<string, any> = {};
		order.forEach((column) => {
			const originalKey = Object.keys(mapping).find(
				(key) => mapping[key] === column,
			);
			if (originalKey && obj[originalKey] !== undefined) {
				mappedObj[column] = obj[originalKey];
			}
		});
		return mappedObj;
	};

	// Flatten and map data
	const flatOutputArray = analysis.flatMap((obj) =>
		obj.analysis.map((item) =>
			mapAndReorder(
				{ ...item, obligation: obj.obligation },
				keyMapping,
				desiredOrder,
			),
		),
	);

	// Generate the worksheet
	const ws = utils.json_to_sheet(flatOutputArray);

	// Set column widths dynamically or use defaults
	const colWidths = desiredOrder.map((_, index) => {
		// Optional: Customize widths based on column index or name
		const widths = [30, 20, 40, 50, 40, 60];
		return { wch: widths[index] || 20 }; // Default width if not defined
	});
	ws['!cols'] = colWidths;

	// Create workbook and save
	const wb = utils.book_new();
	utils.book_append_sheet(wb, ws, 'Data');
	writeFileXLSX(wb, 'Obligation-to-policy.xlsx');
};
export const generateResultPDF = async (
	analysis: IObligationToPolicyOutput[],
) => {
	const doc = new JSPDF();
	const pageWidth = doc.internal.pageSize.getWidth();
	const pageHeight = doc.internal.pageSize.height;
	const textWidth = pageWidth - 2 * margin;
	createPDFDoc(analysis, doc, pageHeight, textWidth);
	doc.save('Obligation-to-policy.pdf');
};
export const generateResultExcel = async (
	analysis: IObligationToPolicyOutput[],
) => {
	generateExcelFile(analysis);
};
